import { ENV } from "@/constants";
import { User } from "@/lib/types/user";
import * as SecureStore from "expo-secure-store";
import * as jose from "jose";
import { Platform } from "react-native";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

function getEncryptionSecret(): Uint8Array {
  const secret = process.env.EXPO_PUBLIC_ENCRYPTION_SECRET;

  if (!secret) {
    if (ENV === "production") {
      throw new Error(
        "EXPO_PUBLIC_ENCRYPTION_SECRET must be set in production",
      );
    }

    return new TextEncoder().encode("secret-at-least-32-chars-long-!!");
  }

  return new TextEncoder().encode(secret);
}

const ENCRYPTION_SECRET = getEncryptionSecret();

const encrypt = async (data: string) => {
  try {
    return await new jose.CompactEncrypt(new TextEncoder().encode(data))
      .setProtectedHeader({ alg: "dir", enc: "A256GCM" })
      .encrypt(ENCRYPTION_SECRET);
  } catch (e) {
    console.error("Encryption failed", e);
    return data;
  }
};

const decrypt = async (data: string) => {
  try {
    const { plaintext } = await jose.compactDecrypt(data, ENCRYPTION_SECRET);
    return new TextDecoder().decode(plaintext);
  } catch (e) {
    console.error("Decryption failed", e);
    return data;
  }
};

const isWeb = Platform.OS === "web";

type AuthStore = {
  user: User | null;
  token: string | null;
  activeBooking: number | null;
  hasCompletedOnboarding: boolean;
  _hasHydrated: boolean;
  logIn: (state: { user: User; token: string }) => void;
  setUser: (user: User) => void;
  setActiveBooking: (id: number | null) => void;
  setHasCompletedOnboarding: (value: boolean) => void;
  logOut: () => void;
  setHasHydrated: (value: boolean) => void;
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      activeBooking: null,
      _hasHydrated: false,
      hasCompletedOnboarding: false,
      logIn: ({ user, token }: { user: User; token: string }) =>
        set({ user, token }),
      setUser: (user: User) => set({ user }),
      setActiveBooking: (id: number | null) => set({ activeBooking: id }),
      setHasCompletedOnboarding: (hasCompletedOnboarding: boolean) =>
        set({ hasCompletedOnboarding }),
      logOut: () =>
        set((state) => ({
          ...state,
          user: null,
          token: null,
          activeBooking: null,
        })),
      setHasHydrated: (value: boolean) => {
        set((state) => {
          return {
            ...state,
            _hasHydrated: value,
          };
        });
      },
    }),
    {
      name: "auth-store",
      storage: isWeb
        ? createJSONStorage(() => ({
            getItem: async (name: string) => {
              const value = localStorage.getItem(name);
              if (!value) return null;
              return await decrypt(value);
            },
            setItem: async (name: string, value: string) => {
              const encryptedValue = await encrypt(value);
              localStorage.setItem(name, encryptedValue);
            },
            removeItem: async (name: string) => localStorage.removeItem(name),
          }))
        : createJSONStorage(() => ({
            setItem: (key: string, value: string) =>
              SecureStore.setItemAsync(key, value),
            getItem: (key: string) => SecureStore.getItemAsync(key),
            removeItem: (key: string) => SecureStore.deleteItemAsync(key),
          })),
      onRehydrateStorage: () => {
        return (state: AuthStore | undefined) => {
          state?.setHasHydrated(true);
        };
      },
    },
  ),
);

export const useUser = () => useAuthStore((state) => state.user);
